import { isPlaceholderGenre } from '~/modules/songs/hooks/use-genre-list';

/**
 * What we know about a song that the song file itself does not say.
 *
 * Two things live here so far: the series a song comes from, which UltraStar has no field for at
 * all, and a genre for the songs that arrived without one. Both are keyed by song id in a file of
 * ours, deliberately *not* written into the song files themselves - the previous fork did that for
 * genres, from a hand-built list of artists, and every one was lost on the next clone of upstream:
 * coverage fell from 89% to 66%. Keeping it here means a song sync cannot take it away.
 */
export interface SongTag {
  /**
   * Artist and title as they read when the entry was written. Carried so the file can be reviewed
   * without cross-referencing ids, and so `validate-song-tags` can notice an id that has come to
   * mean a different song.
   */
  song: string;
  /** A list, because a song can belong to more than one series. */
  series?: string[];
  /** Where the series came from, so a better source can supersede a weaker one knowingly. */
  seriesSource?: SongTagSource;
  /** Only for songs whose file carries no genre - it never overrides one that does. */
  genre?: string;
  genreSource?: SongTagSource;
}

/**
 * Where a tag came from, weakest last.
 *
 * `curated` was decided by a person. `animethemes` came from the AnimeThemes.moe theme database and
 * `musicbrainz` from what MusicBrainz records about the artist. `artist-inference` was taken from
 * the same artist's other songs in this catalogue, which is a guess - a good one for an artist who
 * only ever records in one genre, and no better than that.
 */
export type SongTagSource = 'curated' | 'animethemes' | 'musicbrainz' | 'artist-inference';

export type SongTags = Record<string, SongTag>;

/**
 * A song with what its tag knows merged on. The song's own genre wins, but "None" and its siblings
 * count as nothing: 184 song files say "None" where the tag file knows the answer, and taking the
 * word as written kept every one of those answers off the screen.
 */
export const mergeSongTag = <T extends { genre?: string; series?: string[] }>(song: T, tag: SongTag | undefined): T => {
  if (!tag) return song;

  const ownGenre = song.genre && !isPlaceholderGenre(song.genre) ? song.genre : undefined;

  return { ...song, series: tag.series, genre: ownGenre ?? tag.genre ?? song.genre };
};
